import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, clearAuthToken } from '../api/client';
import OpsLayout from '../components/Layout/OpsLayout';
import './OpsNfps.css';
import './OpsMoneyMovement.css';

const TYPE_OPTIONS = [
  { value: 'all', label: 'All Money Movement' },
  { value: 'fee_sweep', label: 'Fee Sweeps' },
  { value: 'disbursement', label: 'Disbursements' },
];

const DEFAULT_NEEDS_ATTENTION_STATUSES = [
  'failed_retryable',
  'failed_ambiguous',
  'failed_final',
  'processing_stale',
];

const STATUS_OPTIONS = [
  { value: 'pending', label: 'pending' },
  { value: 'processing', label: 'processing' },
  { value: 'processing_stale', label: 'processing_stale' },
  { value: 'failed_retryable', label: 'failed_retryable' },
  { value: 'failed_ambiguous', label: 'failed_ambiguous' },
  { value: 'failed_final', label: 'failed_final' },
  { value: 'succeeded', label: 'succeeded' },
  { value: 'canceled', label: 'canceled' },
];

function formatCurrency(cents = 0, currency = 'USD') {
  const amount = Number(cents || 0) / 100;
  return amount.toLocaleString('en-US', { style: 'currency', currency: String(currency || 'USD').toUpperCase() });
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

function titleizeStatus(status = '') {
  return String(status || '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function statusChipClass(status) {
  if (status === 'succeeded') return 'ops-chip good';
  if (status === 'pending' || status === 'processing') return 'ops-chip info';
  return 'ops-chip bad';
}

function canRetry(status) {
  return status === 'failed_retryable' || status === 'failed_ambiguous';
}

function canReconcile(status) {
  return status === 'failed_ambiguous' || status === 'processing_stale';
}

export default function OpsMoneyMovement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState({
    needsAttention: 0,
    failed: 0,
    ambiguous: 0,
    processingTooLong: 0,
    completedToday: 0,
  });
  const [clearinghouseReconciliation, setClearinghouseReconciliation] = useState({ mismatchCount: 0, reconciliations: [] });
  const [refundOffsets, setRefundOffsets] = useState([]);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [total, setTotal] = useState(0);

  const [typeFilter, setTypeFilter] = useState('all');
  const [statuses, setStatuses] = useState(DEFAULT_NEEDS_ATTENTION_STATUSES);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [destinationAccountId, setDestinationAccountId] = useState('');
  const [causeId, setCauseId] = useState('');
  const [q, setQ] = useState('');
  const [needsAttentionOnly, setNeedsAttentionOnly] = useState(true);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detail, setDetail] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [actionBusyKey, setActionBusyKey] = useState('');

  const selectedStatusSet = useMemo(() => new Set(statuses), [statuses]);

  async function withAuthHandling(fn) {
    try {
      return await fn();
    } catch (err) {
      const status = err?.status;
      if (status === 401) {
        clearAuthToken();
        navigate('/login', { replace: true });
        return null;
      }
      throw err;
    }
  }

  function buildQueryParams({ includePagination = true } = {}) {
    const params = new URLSearchParams();
    params.set('type', typeFilter);
    if (statuses.length) params.set('statuses', statuses.join(','));
    if (dateFrom) params.set('dateFrom', `${dateFrom}T00:00:00.000Z`);
    if (dateTo) params.set('dateTo', `${dateTo}T23:59:59.999Z`);
    if (amountMin !== '') params.set('amountMin', String(amountMin));
    if (amountMax !== '') params.set('amountMax', String(amountMax));
    if (destinationAccountId.trim()) params.set('destinationAccountId', destinationAccountId.trim());
    if (causeId.trim()) params.set('causeId', causeId.trim());
    if (q.trim()) params.set('q', q.trim());
    if (needsAttentionOnly) params.set('needsAttention', 'true');
    if (includePagination) {
      params.set('page', String(page));
      params.set('limit', String(limit));
    }
    return params;
  }

  async function loadSummary() {
    const params = buildQueryParams({ includePagination: false });
    const response = await withAuthHandling(() => apiFetch(`/ops/money-movement/summary?${params.toString()}`));
    if (!response) return;
    setSummary({
      needsAttention: Number(response?.summary?.needsAttention || 0),
      failed: Number(response?.summary?.failed || 0),
      ambiguous: Number(response?.summary?.ambiguous || 0),
      processingTooLong: Number(response?.summary?.processingTooLong || 0),
      completedToday: Number(response?.summary?.completedToday || 0),
    });
  }

  async function loadQueue() {
    setLoading(true);
    setError('');
    try {
      const params = buildQueryParams({ includePagination: true });
      const response = await withAuthHandling(() => apiFetch(`/ops/money-movement?${params.toString()}`));
      if (!response) return;
      setRows(Array.isArray(response.items) ? response.items : []);
      setTotal(Number(response.total || 0));
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed to load money movement queue');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  async function loadClearinghouseMoneyMovement() {
    try {
      const [reconciliation, refunds] = await Promise.all([
        withAuthHandling(() => apiFetch('/ops/clearinghouse/reconciliation?limit=25&exceptionsOnly=true')),
        withAuthHandling(() => apiFetch('/ops/clearinghouse/states?state=refund_offset&limit=25')),
      ]);
      setClearinghouseReconciliation({
        mismatchCount: Number(reconciliation?.mismatchCount || 0),
        reconciliations: Array.isArray(reconciliation?.reconciliations) ? reconciliation.reconciliations : [],
      });
      setRefundOffsets(Array.isArray(refunds?.states) ? refunds.states : []);
    } catch (_err) {
      setClearinghouseReconciliation({ mismatchCount: 0, reconciliations: [] });
      setRefundOffsets([]);
    }
  }

  async function refreshAll() {
    await Promise.all([loadSummary(), loadQueue(), loadClearinghouseMoneyMovement()]);
  }

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, page, limit, statuses.join(','), dateFrom, dateTo, amountMin, amountMax, destinationAccountId, causeId, q, needsAttentionOnly]);

  async function openDetail(row) {
    if (!row) return;
    setSelectedRow(row);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError('');
    setNoteText('');
    try {
      const response = await withAuthHandling(() => apiFetch(`/ops/money-movement/${row.type}/${row.operationId}`));
      if (!response) return;
      setDetail(response);
    } catch (err) {
      setDetailError(err?.data?.error || err?.message || 'Failed to load detail');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  async function runAction(row, action) {
    if (!row) return;
    const key = `${row.operationId}:${action}`;
    setActionBusyKey(key);
    try {
      let endpoint = null;
      if (row.type === 'fee_sweep') {
        endpoint = `/ops/money-movement/fee-sweeps/${row.operationId}/${action}`;
      } else if (row.type === 'disbursement') {
        endpoint = `/ops/money-movement/disbursements/${row.operationId}/${action}`;
      }
      if (!endpoint) return;
      await withAuthHandling(() => apiFetch(endpoint, { method: 'POST' }));
      await refreshAll();
      if (detailOpen && selectedRow?.operationId === row.operationId && selectedRow?.type === row.type) {
        await openDetail(row);
      }
    } catch (err) {
      setError(err?.data?.error || err?.message || `Failed to ${action}`);
    } finally {
      setActionBusyKey('');
    }
  }

  async function addNote() {
    if (!detail || !selectedRow) return;
    const note = noteText.trim();
    if (!note) return;
    setNoteSaving(true);
    try {
      await withAuthHandling(() => apiFetch(`/ops/money-movement/${selectedRow.type}/${selectedRow.operationId}/notes`, {
        method: 'POST',
        body: { note },
      }));
      setNoteText('');
      await openDetail(selectedRow);
    } catch (err) {
      setDetailError(err?.data?.error || err?.message || 'Failed to save note');
    } finally {
      setNoteSaving(false);
    }
  }

  function toggleStatus(status) {
    if (selectedStatusSet.has(status)) {
      setStatuses((prev) => prev.filter((item) => item !== status));
    } else {
      setStatuses((prev) => [...prev, status]);
    }
    setPage(1);
  }

  function resetNeedsAttention() {
    setStatuses(DEFAULT_NEEDS_ATTENTION_STATUSES);
    setTypeFilter('all');
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
    setDestinationAccountId('');
    setCauseId('');
    setQ('');
    setNeedsAttentionOnly(true);
    setPage(1);
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <OpsLayout
      title="Money Movement Ops"
      subtitle="Unified operations queue for fee sweeps and disbursements. Default view shows needs-attention items."
      headerActions={(
        <button type="button" className="ops-btn primary" onClick={refreshAll}>Refresh</button>
      )}
    >
      <div className="ops-nfp-content">
        <section className="ops-nfp-panel" style={{ marginTop: 0 }}>
          <div className="ops-mm-summary-grid">
            <article className="ops-card">
              <div className="ops-card-title">Needs Attention</div>
              <div className="ops-card-value">{summary.needsAttention}</div>
              <div className="ops-card-caption">Actionable now</div>
            </article>
            <article className="ops-card">
              <div className="ops-card-title">Failed</div>
              <div className="ops-card-value">{summary.failed}</div>
              <div className="ops-card-caption">Retryable + Final</div>
            </article>
            <article className="ops-card">
              <div className="ops-card-title">Ambiguous</div>
              <div className="ops-card-value">{summary.ambiguous}</div>
              <div className="ops-card-caption">Reconcile first</div>
            </article>
            <article className="ops-card">
              <div className="ops-card-title">Processing Too Long</div>
              <div className="ops-card-value">{summary.processingTooLong}</div>
              <div className="ops-card-caption">Beyond SLA threshold</div>
            </article>
            <article className="ops-card">
              <div className="ops-card-title">Completed Today</div>
              <div className="ops-card-value">{summary.completedToday}</div>
              <div className="ops-card-caption">Succeeded today</div>
            </article>
            <article className="ops-card">
              <div className="ops-card-title">Clearinghouse Exceptions</div>
              <div className="ops-card-value">{clearinghouseReconciliation.mismatchCount}</div>
              <div className="ops-card-caption">Ledger mismatches</div>
            </article>
            <article className="ops-card">
              <div className="ops-card-title">Refund Offsets</div>
              <div className="ops-card-value">{refundOffsets.length}</div>
              <div className="ops-card-caption">Latest offset records</div>
            </article>
          </div>
        </section>

        <section className="ops-nfp-panel">
          <div className="ops-nfp-panel-header">
            <h3 className="ops-section-title">Clearinghouse Reconciliation</h3>
            <span className={`ops-chip ${clearinghouseReconciliation.mismatchCount > 0 ? 'bad' : 'good'}`}>
              {clearinghouseReconciliation.mismatchCount > 0 ? 'Exceptions' : 'Balanced'}
            </span>
          </div>
          <div className="ops-mm-exception-grid">
            <div className="ops-mm-scroll">
              <table className="ops-nfp-table">
                <thead>
                  <tr>
                    <th>Transaction</th>
                    <th>Brand</th>
                    <th>Expected Debit</th>
                    <th>Ledger Debit</th>
                    <th>Expected Credits</th>
                    <th>Ledger Credits</th>
                    <th>Exception</th>
                  </tr>
                </thead>
                <tbody>
                  {clearinghouseReconciliation.reconciliations.length === 0 ? (
                    <tr><td colSpan={7}>No clearinghouse reconciliation exceptions.</td></tr>
                  ) : clearinghouseReconciliation.reconciliations.map((row) => (
                    <tr key={row.transactionId}>
                      <td>
                        <div>{row.plaidTransactionId || row.transactionId}</div>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>{row.transactionId}</div>
                      </td>
                      <td>{row.matchedBrandId || '—'}</td>
                      <td>{formatCurrency(row.expectedBusinessWalletDebitCents)}</td>
                      <td>{formatCurrency(row.ledgerBusinessWalletDebitCents)}</td>
                      <td>{formatCurrency(Number(row.expectedContributionCreditCents || 0) + Number(row.expectedFeeCreditCents || 0))}</td>
                      <td>{formatCurrency(Number(row.ledgerContributionCreditCents || 0) + Number(row.ledgerFeeCreditCents || 0))}</td>
                      <td>{(row.exceptionTypes || []).join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="ops-mm-scroll">
              <table className="ops-nfp-table">
                <thead>
                  <tr>
                    <th>Refund Offset</th>
                    <th>Brand</th>
                    <th>Subtype</th>
                    <th>Contribution</th>
                    <th>Fee</th>
                    <th>Applied</th>
                  </tr>
                </thead>
                <tbody>
                  {refundOffsets.length === 0 ? (
                    <tr><td colSpan={6}>No refund offsets recorded.</td></tr>
                  ) : refundOffsets.map((row) => (
                    <tr key={row.transactionId}>
                      <td>
                        <div>{row.plaidTransactionId || row.transactionId}</div>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>{row.transactionId}</div>
                      </td>
                      <td>{row.brandName || row.matchedBrandId || '—'}</td>
                      <td>{titleizeStatus(row.refundOffsetSubtype || row.clearinghouseRefundOffsetOriginalState || '') || '—'}</td>
                      <td>{formatCurrency(row.clearinghouseRefundOffsetContributionCents)}</td>
                      <td>{formatCurrency(row.clearinghouseRefundOffsetFeeCents)}</td>
                      <td>{formatDateTime(row.clearinghouseRefundOffsetAppliedAt || row.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="ops-nfp-panel">
          <div className="ops-mm-filter-grid">
            <label className="ops-nfp-field">
              <span>Type</span>
              <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
                {TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="ops-nfp-field">
              <span>Date From</span>
              <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
            </label>
            <label className="ops-nfp-field">
              <span>Date To</span>
              <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
            </label>
            <label className="ops-nfp-field">
              <span>Amount Min ($)</span>
              <input type="number" min="0" step="0.01" value={amountMin} onChange={(e) => { setAmountMin(e.target.value); setPage(1); }} />
            </label>
            <label className="ops-nfp-field">
              <span>Amount Max ($)</span>
              <input type="number" min="0" step="0.01" value={amountMax} onChange={(e) => { setAmountMax(e.target.value); setPage(1); }} />
            </label>
            <label className="ops-nfp-field">
              <span>Destination Stripe Account</span>
              <input value={destinationAccountId} onChange={(e) => { setDestinationAccountId(e.target.value); setPage(1); }} placeholder="acct_..." />
            </label>
            <label className="ops-nfp-field">
              <span>Cause / NFP ID</span>
              <input value={causeId} onChange={(e) => { setCauseId(e.target.value); setPage(1); }} placeholder="Cause ObjectId" />
            </label>
            <label className="ops-nfp-field">
              <span>Search</span>
              <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="entity, reason, stripe id..." />
            </label>
          </div>

          <div className="ops-mm-status-row">
            <div className="ops-mm-status-label">Statuses</div>
            <div className="ops-mm-status-list">
              {STATUS_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`ops-nfp-tab ${selectedStatusSet.has(option.value) ? 'active' : ''}`}
                  onClick={() => toggleStatus(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <label className="ops-mm-toggle">
              <input
                type="checkbox"
                checked={needsAttentionOnly}
                onChange={(e) => { setNeedsAttentionOnly(e.target.checked); setPage(1); }}
              />
              Needs manual action only
            </label>
          </div>

          <div className="ops-nfp-filter-actions">
            <button type="button" className="ops-btn primary" onClick={resetNeedsAttention}>Default Needs Attention</button>
            <button type="button" className="ops-btn" onClick={() => setStatuses([])}>All Statuses</button>
          </div>
        </section>

        <section className="ops-nfp-panel">
          <div className="ops-nfp-panel-header">
            <h3 className="ops-section-title">Queue</h3>
            <div className="ops-mm-pagination">
              <button type="button" className="ops-btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
              <span>Page {page} / {totalPages} ({total} items)</span>
              <button type="button" className="ops-btn" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
            </div>
          </div>

          {loading && <div className="ops-message">Loading money movement queue…</div>}
          {!loading && error && <div className="ops-message error">{error}</div>}
          {!loading && !error && (
            <div className="ops-nfp-table-wrap">
              <table className="ops-nfp-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Entity</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Attempt Count</th>
                    <th>Last Attempt</th>
                    <th>Reason</th>
                    <th>Created / Scheduled</th>
                    <th>Destination</th>
                    <th>Stripe Object</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={11}>No money movement items currently need attention.</td>
                    </tr>
                  )}
                  {rows.map((row) => {
                    const busyRetry = actionBusyKey === `${row.operationId}:retry`;
                    const busyReconcile = actionBusyKey === `${row.operationId}:reconcile`;
                    return (
                      <tr key={row.id}>
                        <td>{row.type === 'fee_sweep' ? 'Fee Sweep' : 'Disbursement'}</td>
                        <td>
                          <div>{row.entityName || '—'}</div>
                          <div style={{ fontSize: 12, opacity: 0.7 }}>{row.logicalOperationId}</div>
                        </td>
                        <td>{formatCurrency(row.amountCents, row.currency)}</td>
                        <td><span className={statusChipClass(row.status)}>{titleizeStatus(row.status)}</span></td>
                        <td>{Number(row.attemptCount || 0)}</td>
                        <td>{formatDateTime(row.lastAttemptAt)}</td>
                        <td>{row.reasonSummary || '—'}</td>
                        <td>
                          <div>Created: {formatDateTime(row.createdAt)}</div>
                          <div style={{ fontSize: 12, opacity: 0.7 }}>Scheduled: {formatDate(row.scheduledAt)}</div>
                        </td>
                        <td>{row.destinationAccountId || '—'}</td>
                        <td>{row.stripeObjectId || '—'}</td>
                        <td>
                          <div className="ops-mm-actions">
                            <button type="button" className="ops-btn" onClick={() => openDetail(row)}>View</button>
                            <button type="button" className="ops-btn primary" disabled={!canRetry(row.status) || busyRetry} onClick={() => runAction(row, 'retry')}>
                              {busyRetry ? 'Retrying…' : 'Retry'}
                            </button>
                            <button type="button" className="ops-btn" disabled={!canReconcile(row.status) || busyReconcile} onClick={() => runAction(row, 'reconcile')}>
                              {busyReconcile ? 'Reconciling…' : 'Reconcile'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {detailOpen && (
          <section className="ops-nfp-panel ops-mm-detail-panel">
            <div className="ops-nfp-panel-header">
              <h3 className="ops-section-title">Detail</h3>
              <button type="button" className="ops-btn" onClick={() => { setDetailOpen(false); setDetail(null); setSelectedRow(null); }}>Close</button>
            </div>
            {detailLoading && <div className="ops-message">Loading detail…</div>}
            {!detailLoading && detailError && <div className="ops-message error">{detailError}</div>}
            {!detailLoading && !detailError && detail && (
              <div className="ops-mm-detail-grid">
                <section className="ops-mm-detail-section">
                  <h4>Overview</h4>
                  <div className="ops-kv"><div className="k">Type</div><div className="v">{detail.type === 'fee_sweep' ? 'Fee Sweep' : 'Disbursement'}</div></div>
                  <div className="ops-kv"><div className="k">Logical Operation ID</div><div className="v">{detail?.overview?.logicalOperationId || '—'}</div></div>
                  <div className="ops-kv"><div className="k">Status</div><div className="v">{titleizeStatus(detail?.overview?.status || '') || '—'}</div></div>
                  <div className="ops-kv"><div className="k">Amount</div><div className="v">{formatCurrency(detail?.overview?.amountCents || 0, detail?.overview?.currency || 'usd')}</div></div>
                  <div className="ops-kv"><div className="k">Created</div><div className="v">{formatDateTime(detail?.overview?.createdAt)}</div></div>
                  <div className="ops-kv"><div className="k">Updated</div><div className="v">{formatDateTime(detail?.overview?.updatedAt)}</div></div>
                  <div className="ops-kv"><div className="k">Completed</div><div className="v">{formatDateTime(detail?.overview?.completedAt)}</div></div>
                  <div className="ops-kv"><div className="k">Destination Account</div><div className="v">{detail?.overview?.destinationAccountId || '—'}</div></div>
                  <div className="ops-kv"><div className="k">Stripe Object</div><div className="v">{detail?.overview?.stripeObjectId || '—'}</div></div>
                </section>

                <section className="ops-mm-detail-section">
                  <h4>Attempt History</h4>
                  <div className="ops-mm-scroll">
                    <table className="ops-nfp-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Status</th>
                          <th>Started</th>
                          <th>Finished</th>
                          <th>Stripe Key</th>
                          <th>Request ID</th>
                          <th>Stripe Object</th>
                          <th>Error</th>
                          <th>Reconciliation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.attempts || []).length === 0 && (
                          <tr><td colSpan={9}>No attempts recorded.</td></tr>
                        )}
                        {(detail.attempts || []).map((attempt) => (
                          <tr key={attempt.id || `${attempt.beneficiaryRole || 'na'}-${attempt.attemptNumber}-${attempt.startedAt || ''}`}>
                            <td>{attempt.attemptNumber}</td>
                            <td>{attempt.status || '—'}</td>
                            <td>{formatDateTime(attempt.startedAt)}</td>
                            <td>{formatDateTime(attempt.finishedAt)}</td>
                            <td>{attempt.stripeIdempotencyKey || '—'}</td>
                            <td>{attempt.stripeRequestId || '—'}</td>
                            <td>{attempt.stripeObjectId || '—'}</td>
                            <td>{attempt.stripeErrorCode || attempt.stripeErrorMessage || '—'}</td>
                            <td>{attempt.reconciliationStatus || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="ops-mm-detail-section">
                  <h4>Reconciliation</h4>
                  <div className="ops-kv"><div className="k">Was run</div><div className="v">{detail?.reconciliation?.wasRun ? 'yes' : 'no'}</div></div>
                  <div className="ops-kv"><div className="k">Checked At</div><div className="v">{formatDateTime(detail?.reconciliation?.checkedAt)}</div></div>
                  <div className="ops-kv"><div className="k">Outcome</div><div className="v">{detail?.reconciliation?.outcome || '—'}</div></div>
                  <div className="ops-kv"><div className="k">Matched Stripe Object</div><div className="v">{detail?.reconciliation?.matchedStripeObjectId || '—'}</div></div>
                </section>

                {detail.type === 'fee_sweep' && (
                  <section className="ops-mm-detail-section">
                    <h4>Fee Sweep Context</h4>
                    <div className="ops-kv"><div className="k">Window Start</div><div className="v">{formatDateTime(detail?.typeSpecific?.sweepWindow?.periodStart)}</div></div>
                    <div className="ops-kv"><div className="k">Window End</div><div className="v">{formatDateTime(detail?.typeSpecific?.sweepWindow?.periodEnd)}</div></div>
                    <div className="ops-kv"><div className="k">Trigger</div><div className="v">{detail?.typeSpecific?.trigger || '—'}</div></div>
                    <div className="ops-kv"><div className="k">On Hold Reason</div><div className="v">{detail?.typeSpecific?.onHoldReason || '—'}</div></div>
                    <div className="ops-kv"><div className="k">Error Message</div><div className="v">{detail?.typeSpecific?.errorMessage || '—'}</div></div>
                  </section>
                )}

                {detail.type === 'disbursement' && (
                  <section className="ops-mm-detail-section">
                    <h4>Disbursement Context</h4>
                    <div className="ops-kv"><div className="k">Period Start</div><div className="v">{formatDateTime(detail?.typeSpecific?.periodStart)}</div></div>
                    <div className="ops-kv"><div className="k">Period End</div><div className="v">{formatDateTime(detail?.typeSpecific?.periodEnd)}</div></div>
                    <div className="ops-kv"><div className="k">Trigger</div><div className="v">{detail?.typeSpecific?.trigger || '—'}</div></div>
                    <div className="ops-kv"><div className="k">Payout Intent</div><div className="v">{detail?.typeSpecific?.payoutIntentMode || '—'}</div></div>
                    <div className="ops-kv"><div className="k">On Hold Reason</div><div className="v">{detail?.typeSpecific?.onHoldReason || '—'}</div></div>
                    <div className="ops-mm-scroll" style={{ marginTop: 10 }}>
                      <table className="ops-nfp-table">
                        <thead>
                          <tr>
                            <th>Role</th>
                            <th>Destination</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Transfer</th>
                            <th>Payout</th>
                            <th>Attempts</th>
                            <th>Last Stripe Key</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detail?.typeSpecific?.beneficiaries || []).length === 0 && (
                            <tr><td colSpan={8}>No beneficiary rows.</td></tr>
                          )}
                          {(detail?.typeSpecific?.beneficiaries || []).map((b, idx) => (
                            <tr key={`${b.role || 'benef'}-${b.stripeAccountId || 'acct'}-${idx}`}>
                              <td>{b.role || '—'}</td>
                              <td>{b.stripeAccountId || '—'}</td>
                              <td>{formatCurrency(b.amountCents, detail?.overview?.currency || 'usd')}</td>
                              <td>{b.transferStatus || '—'}</td>
                              <td>{b.transferId || '—'}</td>
                              <td>{b.payoutId || '—'}</td>
                              <td>{Number(b.attemptCount || 0)}</td>
                              <td>{b.lastStripeIdempotencyKey || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                <section className="ops-mm-detail-section">
                  <h4>Ledger</h4>
                  <div className="ops-mm-scroll">
                    <table className="ops-nfp-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Dir</th>
                          <th>Amount</th>
                          <th>Source</th>
                          <th>Idempotency Key</th>
                          <th>Effective</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.ledger || []).length === 0 && (
                          <tr><td colSpan={7}>No ledger rows linked.</td></tr>
                        )}
                        {(detail.ledger || []).map((row) => (
                          <tr key={row.id}>
                            <td>{row.accountType || '—'}</td>
                            <td>{row.direction || '—'}</td>
                            <td>{formatCurrency(row.amountCents, row.currency || 'usd')}</td>
                            <td>{row.sourceType || '—'}</td>
                            <td>{row.idempotencyKey || '—'}</td>
                            <td>{formatDateTime(row.effectiveDate)}</td>
                            <td>{formatDateTime(row.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="ops-mm-detail-section">
                  <h4>Notes & Audit Trail</h4>
                  <div className="ops-mm-note-form">
                    <textarea
                      rows={3}
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Add operational note…"
                    />
                    <button type="button" className="ops-btn primary" disabled={noteSaving || !noteText.trim()} onClick={addNote}>
                      {noteSaving ? 'Saving…' : 'Add Note'}
                    </button>
                  </div>
                  <div className="ops-mm-scroll">
                    <table className="ops-nfp-table">
                      <thead>
                        <tr>
                          <th>When</th>
                          <th>Action</th>
                          <th>Actor</th>
                          <th>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.notes || []).length === 0 && (
                          <tr><td colSpan={4}>No notes yet.</td></tr>
                        )}
                        {(detail.notes || []).map((item) => (
                          <tr key={item.id}>
                            <td>{formatDateTime(item.createdAt)}</td>
                            <td>{item.action || item.eventType || '—'}</td>
                            <td>{item.actorUserId || '—'}</td>
                            <td>{item.note || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>
            )}
          </section>
        )}
      </div>
    </OpsLayout>
  );
}
